/** biome-ignore-all lint/style/noMagicNumbers: valid constant */
import { relations } from "drizzle-orm";
import { pgTable, unique } from "drizzle-orm/pg-core";
import { createInsertSchema, createUpdateSchema } from "drizzle-valibot";
import {
  array,
  boolean,
  description,
  intersect,
  literal,
  maxLength,
  minLength,
  object,
  omit,
  picklist,
  pipe,
  string,
  union,
  url,
  uuid,
} from "valibot";
import { slugField } from "./validators";

/**
 * Whether a project was delivered for a client or is an exploration.
 *
 * The design labels every entry with one of these and the client's own export
 * says to preserve the concept label unless delivery is verified, so it is a
 * column rather than a convention: a mislabelled concept claims work that was
 * never shipped.
 */
export const PROJECT_STATUSES = ["delivered", "concept"] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

export const Project = pgTable("project", (t) => ({
  createdAt: t.timestamp().defaultNow().notNull(),
  demoUrl: t.varchar({ length: 255 }),
  githubUrl: t.varchar({ length: 255 }),
  id: t.uuid().notNull().primaryKey().defaultRandom(),
  imageUrl: t.varchar({ length: 255 }),
  isDraft: t.boolean().notNull().default(false),
  isFeatured: t.boolean().notNull().default(false),
  stacks: t.text().array(),
  status: t
    .varchar({ length: 16 })
    .$type<ProjectStatus>()
    .notNull()
    .default("concept"),
  updatedAt: t
    .timestamp({ mode: "date", withTimezone: true })
    .$onUpdate(() => new Date()),
}));

export const projectTranslations = pgTable(
  "project_translations",
  (t) => ({
    content: t.text(),
    /** The line under the cover: what the screenshot is showing. */
    coverCaption: t.varchar({ length: 255 }),
    createdAt: t.timestamp().defaultNow().notNull(),
    description: t.varchar({ length: 255 }),
    id: t.uuid().notNull().primaryKey().defaultRandom(),
    /** "Hospitality" — the sector the work was for. */
    industry: t.varchar({ length: 120 }),
    locale: t.varchar({ length: 5 }).notNull(),
    projectId: t
      .uuid()
      .references(() => Project.id, { onDelete: "cascade" })
      .notNull(),
    slug: t.varchar({ length: 255 }).notNull(),
    title: t.varchar({ length: 255 }).notNull(),
    updatedAt: t
      .timestamp({ mode: "date", withTimezone: true })
      .$onUpdate(() => new Date()),
    /** "Booking platform" — what we actually did. */
    workType: t.varchar({ length: 120 }),
  }),
  (t) => [
    unique("project_translations_project_id_locale_unique").on(
      t.projectId,
      t.locale
    ),
    unique("project_translations_slug_locale_unique").on(t.slug, t.locale),
  ]
);

export const projectRelations = relations(Project, (t) => ({
  translations: t.many(projectTranslations),
}));

export const projectTranslationRelations = relations(
  projectTranslations,
  (t) => ({
    project: t.one(Project, {
      fields: [projectTranslations.projectId],
      references: [Project.id],
    }),
  })
);

const RequiredTitleField = pipe(
  string(),
  minLength(1, "Title is required"),
  maxLength(255, "Title cannot exceed 255 characters")
);

const OptionalTitleField = union([
  pipe(string(), maxLength(255, "Title cannot exceed 255 characters")),
  literal(""),
]);

const OptionalSlugField = union([slugField, literal("")]);

const DescriptionField = union([
  pipe(string(), maxLength(255, "Description cannot exceed 255 characters")),
  literal(""),
]);

const ContentField = union([string(), literal("")]);

/** The short case facts beside a project title. Optional on both locales. */
const FactField = union([
  pipe(string(), maxLength(120, "Cannot exceed 120 characters")),
  literal(""),
]);

const CaptionField = union([
  pipe(string(), maxLength(255, "Cannot exceed 255 characters")),
  literal(""),
]);

const StatusField = picklist(PROJECT_STATUSES);

export const EnProjectTranslationSchema = object({
  content: ContentField,
  coverCaption: CaptionField,
  description: DescriptionField,
  industry: FactField,
  slug: slugField,
  title: RequiredTitleField,
  workType: FactField,
});

export const LvProjectTranslationSchema = object({
  content: ContentField,
  coverCaption: CaptionField,
  description: DescriptionField,
  industry: FactField,
  slug: OptionalSlugField,
  title: OptionalTitleField,
  workType: FactField,
});

export const ProjectTranslationsMapSchema = object({
  en: EnProjectTranslationSchema,
  lv: LvProjectTranslationSchema,
});

export const ProjectBaseSchema = object({
  demoUrl: union([
    pipe(string(), url("Please enter a valid demo URL"), maxLength(255)),
    literal(""),
  ]),
  githubUrl: union([
    pipe(string(), url("Please enter a valid GitHub URL"), maxLength(255)),
    literal(""),
  ]),
  isDraft: union([boolean(), literal(false)]),
  isFeatured: union([boolean(), literal(false)]),
  stacks: array(string()),
  status: StatusField,
  thumbnail: pipe(string(), description("File upload for project thumbnail")),
  translations: ProjectTranslationsMapSchema,
});

export const CreateProjectSchema = intersect([
  omit(
    createInsertSchema(Project, {
      demoUrl: ProjectBaseSchema.entries.demoUrl,
      githubUrl: ProjectBaseSchema.entries.githubUrl,
      isDraft: ProjectBaseSchema.entries.isDraft,
      isFeatured: ProjectBaseSchema.entries.isFeatured,
      status: ProjectBaseSchema.entries.status,
    }),
    ["id", "createdAt", "updatedAt"]
  ),
  object({
    thumbnail: ProjectBaseSchema.entries.thumbnail,
    translations: ProjectTranslationsMapSchema,
  }),
]);

export const UpdateProjectSchema = intersect([
  omit(
    createUpdateSchema(Project, {
      demoUrl: ProjectBaseSchema.entries.demoUrl,
      githubUrl: ProjectBaseSchema.entries.githubUrl,
      id: pipe(string(), uuid()),
      isDraft: ProjectBaseSchema.entries.isDraft,
      isFeatured: ProjectBaseSchema.entries.isFeatured,
      status: ProjectBaseSchema.entries.status,
    }),
    ["createdAt", "updatedAt"]
  ),
  object({
    thumbnail: ProjectBaseSchema.entries.thumbnail,
    translations: ProjectTranslationsMapSchema,
  }),
]);
